import React, { useEffect, useRef, useState } from "react";
import { Sparkles, Send, Loader2, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/lib/api";
import { Input } from "@/components/ui/input";

const SUGGESTIONS = [
  "Berapa booking hari ini dan siapa saja?",
  "Unit mana yang sedang rusak atau maintenance?",
  "Berapa pendapatan bulan ini?",
  "Booking mana yang belum bayar?",
  "Ada yang lewat batas pengembalian?",
];

export const streamAI = async (path, body, onDelta) => {
  const res = await fetch(`${API}${path}`, {
    method: "POST", credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok || !res.body) throw new Error(`AI error ${res.status}`);
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buf = "", sessionId = null;
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buf += decoder.decode(value, { stream: true });
    const parts = buf.split("\n\n");
    buf = parts.pop();
    for (const p of parts) {
      if (!p.startsWith("data: ")) continue;
      const evt = JSON.parse(p.slice(6));
      if (evt.delta) onDelta(evt.delta);
      if (evt.error) throw new Error(evt.error);
      if (evt.session_id) sessionId = evt.session_id;
    }
  }
  return sessionId;
};

export default function AiAssistant() {
  const [sid, setSid] = useState(() => localStorage.getItem("ai_sid") || `ses_${Date.now()}`);
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);

  useEffect(() => { localStorage.setItem("ai_sid", sid); }, [sid]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs, busy]);

  const ask = async (question) => {
    const q = (question ?? text).trim();
    if (!q || busy) return;
    setText(""); setBusy(true);
    setMsgs((m) => [...m, { role: "user", text: q }, { role: "assistant", text: "" }]);
    try {
      await streamAI("/ai/assistant", { session_id: sid, message: q }, (d) =>
        setMsgs((m) => {
          const copy = [...m];
          copy[copy.length - 1] = { role: "assistant", text: copy[copy.length - 1].text + d };
          return copy;
        }));
    } catch (e) {
      toast.error("AI gagal menjawab. Coba lagi.");
      setMsgs((m) => m.slice(0, -1));
    } finally { setBusy(false); }
  };

  const reset = () => { setMsgs([]); setSid(`ses_${Date.now()}`); };

  return (
    <div data-testid="admin-ai" className="max-w-3xl">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-display text-3xl tracking-tight flex items-center gap-2">
            <Sparkles className="text-lm-gold" size={24} /> Asisten AI
          </h1>
          <p className="mt-1 text-sm text-ink-soft">
            Tanya apa saja soal operasional — booking, unit, pembayaran, pendapatan. AI hanya menjawab dari data Anda.
          </p>
        </div>
        <button data-testid="ai-reset" onClick={reset}
          className="rounded-md px-3 py-2 text-sm border border-black/15 inline-flex items-center gap-2">
          <RotateCcw size={14} /> Percakapan baru
        </button>
      </div>

      {msgs.length === 0 && (
        <div className="mt-5 flex flex-wrap gap-2" data-testid="ai-suggestions">
          {SUGGESTIONS.map((s) => (
            <button key={s} onClick={() => ask(s)} data-testid={`ai-suggest-${s.slice(0, 12)}`}
              className="text-xs px-3 py-2 rounded-full border border-black/15 hover:border-lm-red text-left">{s}</button>
          ))}
        </div>
      )}

      <div className="mt-5 space-y-3" data-testid="ai-thread">
        {msgs.map((m, i) => (
          <div key={`m-${i}`} data-testid={`ai-msg-${m.role}-${i}`}
            className={`rounded-lg p-4 text-sm whitespace-pre-wrap ${m.role === "user"
              ? "bg-ink text-cream ml-auto max-w-[85%]" : "bg-white border border-black/10 max-w-[95%]"}`}>
            {m.text || <Loader2 className="animate-spin" size={16} />}
          </div>
        ))}
        <div ref={endRef} />
      </div>

      <div className="mt-5 flex gap-2 sticky bottom-0 bg-cream py-3">
        <Input data-testid="ai-input" placeholder="Tulis pertanyaan…" value={text}
          onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && ask()} />
        <button data-testid="ai-send" onClick={() => ask()} disabled={busy}
          className="lm-btn-primary rounded-md px-5 py-2.5 text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-60">
          {busy ? <Loader2 className="animate-spin" size={15} /> : <Send size={15} />} Kirim
        </button>
      </div>
    </div>
  );
}
